// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';
import {
  types as t,
  usePropsValidation,
} from '../validation/runtime-checks/index.js';
import color from '../color.js';
import bullet from './bullet.svg';
import { Heading } from '../Heading.js';
import Latex from '../Latex.js';
import { Language, English, Spanish } from '../languages.js';
import { LatexTextAtom } from '../LatexTextAtom.js';

export function SSOLearningTargetsList(props: {|
  children: $ReadOnlyArray<Element<typeof SSOLearningTargetsList>>,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    SSOLearningTargetsList,
  );

  if (failedValidation) return null;
  const plural = props.children.length > 1;

  return (
    <>
      <Heading>
        <Language>
          <English>Learning target{plural && 's'}</English>
          <Spanish>
            {plural ? 'Objetivos de aprendizaje' : 'Objetivo de aprendizaje'}
          </Spanish>
        </Language>
      </Heading>
      <div>
        <p style={{ color: color.cloudBurst }}>
          <Language>
            <English>I can:</English>
            <Spanish>Yo puedo:</Spanish>
          </Language>
        </p>
        <ul style={{ paddingLeft: 16 }}>{props.children}</ul>
      </div>
    </>
  );
}

SSOLearningTargetsList.spec = {
  children: t.children(t.minimumCount(1, t.elementType(SSOLearningTarget))),
};

export function SSOLearningTarget(props: {|
  children: ChildrenArray<
    string | Element<typeof Latex> | Element<typeof LatexTextAtom>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(props, SSOLearningTarget);

  if (failedValidation) return null;

  return (
    <li
      style={{
        color: color.shuttleGray,
        listStyleImage: `url(${bullet})`,
        marginBottom: 10,
        listStylePosition: 'outside',
      }}
    >
      {props.children}
    </li>
  );
}

SSOLearningTarget.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      t.string,
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
    ),
  ),
};

export function SOKeyVocabularyList(props: {|
  children: $ReadOnlyArray<Element<typeof SOKeyVocabularyItem>>,
|}) {
  const { failedValidation } = usePropsValidation(props, SOKeyVocabularyList);

  if (failedValidation) return null;

  return (
    <div>
      <h2
        style={{
          fontWeight: 600,
          color: color.cloudBurst,
          fontSize: 16,
          lineHeight: '24px',
          marginBottom: 16,
        }}
      >
        Key vocabulary
      </h2>
      <ul style={{ padding: 16 }}>{props.children}</ul>
    </div>
  );
}

SOKeyVocabularyList.spec = {
  children: t.minimumCount(1, t.elementType(SOKeyVocabularyItem)),
};

export function SOKeyVocabularyItem(props: {|
  children: ChildrenArray<
    string | Element<typeof Latex> | Element<typeof LatexTextAtom>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(props, SOKeyVocabularyItem);

  if (failedValidation) return null;

  return (
    <li
      style={{
        color: color.shuttleGray,
        listStyleImage: `url(${bullet})`,
        marginBottom: 10,
        listStylePosition: 'outside',
      }}
    >
      {props.children}
    </li>
  );
}

SOKeyVocabularyItem.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      t.string,
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
    ),
  ),
};
